import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  Calendar,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Download,
  ExternalLink,
  FileText,
  Image,
  Lock,
  Paperclip,
  Pencil,
  User,
} from 'lucide-react';
import { AppLayout } from '../../../components/layout/app-layout';
import { Breadcrumb } from '../../../components/layout/breadcrumb';
import { GradientAvatar } from '../../../components/shared/gradient-avatar';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { usePermissions } from '../../../hooks/use-permissions';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth.store';

export const Route = createFileRoute('/inspections/$inspectionId/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('inspection:view')) throw redirect({ to: '/dashboard' });
  },
  component: InspectionDetailPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface Attachment {
  id: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  signedUrl?: string | null;
  uploadedAt: string;
  uploadedBy: { name: string };
}

interface InspectionDetail {
  id: string;
  status: string;
  findings: string | null;
  recommendations: string | null;
  nextAction: string | null;
  assignedAt: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  assignedInspectorId: string;
  assignedInspector: { id: string; name: string; email: string };
  request: {
    id: string;
    referenceNo: string;
    status: string;
    totalFee: number | null;
    createdAt: string;
    requestType: { name: string; code: string } | null;
    account: { id: string; name: string } | null;
    contact: { fullName: string; email: string } | null;
  };
  attachments: Attachment[];
}

// ── API fetch ─────────────────────────────────────────────────────────────────

async function fetchInspection(id: string): Promise<InspectionDetail> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`/api/v1/inspections/${id}`, {
    headers: { Authorization: `Bearer ${token ?? ''}` },
  });
  if (!res.ok) throw new Error('Failed to load inspection');
  return res.json();
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function fmtSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const STATUS_CONFIG = {
  IN_PROGRESS: {
    label: 'In Progress',
    dot: 'bg-amber-400',
    cls: 'border-amber-200 bg-amber-50 text-amber-700',
    icon: Clock,
  },
  COMPLETED: {
    label: 'Completed',
    dot: 'bg-success-500',
    cls: 'border-success-200 bg-success-50 text-success-700',
    icon: CheckCircle2,
  },
  PENDING: {
    label: 'Pending',
    dot: 'bg-gray-400',
    cls: 'border-gray-200 bg-gray-50 text-gray-500',
    icon: Clock,
  },
};

const PERMIT_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  SUBMITTED_FOR_INSPECTION: 'Submitted for Inspection',
  INSPECTION_IN_PROGRESS: 'Inspection In Progress',
  PLEDGE_STUDY_REQUEST: 'Pledge Study Request',
  APPROVAL_REQUEST: 'Approval Request',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  PAYMENT_PROCESS: 'Payment Process',
  PAID: 'Paid',
  PERMIT_ISSUED: 'Permit Issued',
  SENT_TO_SAP: 'Sent to SAP',
};

function FileIcon({ mimeType }: { mimeType: string }) {
  if (mimeType === 'application/pdf') return <FileText size={18} className="text-red-500" />;
  if (mimeType.startsWith('image/')) return <Image size={18} className="text-blue-500" />;
  return <FileText size={18} className="text-gray-400" />;
}

// ── Section card ──────────────────────────────────────────────────────────────

function SectionCard({
  icon,
  iconBg,
  title,
  subtitle,
  children,
  action,
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <div className="flex items-center gap-3">
          <div
            className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', iconBg)}
          >
            {icon}
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{title}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function InspectionDetailPage() {
  const { inspectionId } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { can } = usePermissions();

  const {
    data: inspection,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['inspection', inspectionId],
    queryFn: () => fetchInspection(inspectionId),
  });

  const isEditable =
    inspection?.status === 'IN_PROGRESS' &&
    inspection.assignedInspectorId === user?.id &&
    can('permit:create-inspection');

  const statusCfg = inspection
    ? (STATUS_CONFIG[inspection.status as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.PENDING)
    : null;

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-24 w-full rounded-2xl" />
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
            <div className="space-y-4">
              <Skeleton className="h-40 w-full rounded-2xl" />
              <Skeleton className="h-40 w-full rounded-2xl" />
              <Skeleton className="h-32 w-full rounded-2xl" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-56 w-full rounded-2xl" />
              <Skeleton className="h-32 w-full rounded-2xl" />
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (isError || !inspection) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <AlertCircle size={40} className="text-error-400" />
          <p className="text-sm font-semibold text-gray-600">Inspection not found</p>
          <Button variant="outline" size="sm" onClick={() => navigate({ to: '/inspections' })}>
            Back to Inspections
          </Button>
        </div>
      </AppLayout>
    );
  }

  const breadcrumbItems = [
    { label: 'Inspections', href: '/inspections' },
    { label: inspection.request.referenceNo },
  ];

  return (
    <AppLayout>
      <Breadcrumb items={breadcrumbItems} />

      {/* ── Hero header card ── */}
      <div className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="h-1 bg-gradient-to-r from-teal-400 via-emerald-500 to-green-400" />
        <div className="flex items-start justify-between gap-4 p-6">
          <div className="flex items-start gap-4">
            <button
              type="button"
              onClick={() => navigate({ to: '/inspections' })}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-700 shrink-0 mt-0.5"
            >
              <ArrowLeft size={16} />
            </button>
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 shadow-md">
                <ClipboardCheck size={24} className="text-white" strokeWidth={1.5} />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-1.5">
                  <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                    {inspection.request.referenceNo}
                  </h1>
                  {statusCfg && (
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
                        statusCfg.cls,
                      )}
                    >
                      <span className={cn('h-1.5 w-1.5 rounded-full', statusCfg.dot)} />
                      {statusCfg.label}
                    </span>
                  )}
                  {inspection.nextAction && (
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                        inspection.nextAction === 'PLEDGE_STUDY'
                          ? 'border-rose-200 bg-rose-50 text-rose-600'
                          : 'border-blue-200 bg-blue-50 text-blue-600',
                      )}
                    >
                      → {inspection.nextAction === 'PLEDGE_STUDY' ? 'Pledge Study' : 'Approval'}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                  {inspection.request.requestType && (
                    <span className="flex items-center gap-1.5">
                      <FileText size={13} className="text-gray-400" />
                      {inspection.request.requestType.name}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5">
                    <User size={13} className="text-gray-400" />
                    {inspection.assignedInspector.name}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Calendar size={13} className="text-gray-400" />
                    Assigned {fmtDate(inspection.assignedAt)}
                  </span>
                  {inspection.completedAt && (
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 size={13} className="text-success-500" />
                      Completed {fmtDate(inspection.completedAt)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {isEditable && (
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  navigate({ to: '/inspections/$inspectionId/edit', params: { inspectionId } })
                }
              >
                <Pencil size={14} />
                Edit Inspection
              </Button>
            )}
            <Link
              to="/permits/$permitId"
              params={{ permitId: inspection.request.id }}
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <ExternalLink size={14} />
              View Permit
            </Link>
          </div>
        </div>
      </div>

      {/* ── Two-column body ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">
        {/* ── Left: inspection content ── */}
        <div className="space-y-4">
          {/* Findings */}
          <SectionCard
            icon={<ClipboardCheck size={16} className="text-teal-600" />}
            iconBg="bg-teal-50"
            title="Findings"
            subtitle="Inspector's field observations"
          >
            {inspection.findings ? (
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {inspection.findings}
              </p>
            ) : (
              <p className="text-sm text-gray-400 italic">No findings recorded yet.</p>
            )}
          </SectionCard>

          {/* Recommendations */}
          <SectionCard
            icon={<CheckCircle2 size={16} className="text-emerald-600" />}
            iconBg="bg-emerald-50"
            title="Recommendations"
            subtitle="Action items and recommendations"
          >
            {inspection.recommendations ? (
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {inspection.recommendations}
              </p>
            ) : (
              <p className="text-sm text-gray-400 italic">No recommendations recorded yet.</p>
            )}
          </SectionCard>

          {/* Attachments */}
          <SectionCard
            icon={<Paperclip size={16} className="text-violet-600" />}
            iconBg="bg-violet-50"
            title="Attachments"
            subtitle={`${inspection.attachments.length} file${inspection.attachments.length !== 1 ? 's' : ''} uploaded`}
          >
            {inspection.attachments.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No attachments uploaded.</p>
            ) : (
              <div className="space-y-2">
                {inspection.attachments.map((att) => (
                  <div
                    key={att.id}
                    className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 hover:bg-gray-100 transition-colors"
                  >
                    <FileIcon mimeType={att.mimeType} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{att.fileName}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {fmtSize(att.fileSizeBytes)} · {att.uploadedBy.name} ·{' '}
                        {fmtDate(att.uploadedAt)}
                      </p>
                    </div>
                    {att.signedUrl ? (
                      <a
                        href={att.signedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-brand-300 hover:text-brand-500 transition-colors"
                        title="Download"
                      >
                        <Download size={14} />
                      </a>
                    ) : (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-300">
                        <Lock size={13} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        {/* ── Right sidebar ── */}
        <div className="space-y-4">
          {/* Linked permit */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-5 py-4">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                Linked Permit
              </p>
            </div>
            <div className="px-5 py-4 space-y-3">
              <Link
                to="/permits/$permitId"
                params={{ permitId: inspection.request.id }}
                className="flex items-center gap-2 group"
              >
                <span className="text-sm font-bold text-gray-900 group-hover:text-brand-600 transition-colors">
                  {inspection.request.referenceNo}
                </span>
                <ExternalLink
                  size={12}
                  className="text-gray-400 group-hover:text-brand-500 transition-colors"
                />
              </Link>

              {inspection.request.requestType && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">
                    Type
                  </p>
                  <p className="text-sm text-gray-700">{inspection.request.requestType.name}</p>
                </div>
              )}

              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">
                  Status
                </p>
                <p className="text-sm font-medium text-gray-700">
                  {PERMIT_STATUS_LABELS[inspection.request.status] ?? inspection.request.status}
                </p>
              </div>

              {inspection.request.account && (
                <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                  <Building2 size={13} className="text-gray-400 shrink-0" />
                  <p className="text-sm text-gray-700 truncate">
                    {inspection.request.account.name}
                  </p>
                </div>
              )}

              {inspection.request.contact && (
                <div className="flex items-center gap-2">
                  <User size={13} className="text-gray-400 shrink-0" />
                  <p className="text-sm text-gray-700 truncate">
                    {inspection.request.contact.fullName}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Inspector */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-5 py-4">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Inspector</p>
            </div>
            <div className="flex items-center gap-3 px-5 py-4">
              <GradientAvatar name={inspection.assignedInspector.name} size="md" shape="circle" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {inspection.assignedInspector.name}
                </p>
                <p className="text-xs text-gray-400 truncate mt-0.5">
                  {inspection.assignedInspector.email}
                </p>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
              Details
            </p>
            <div className="space-y-3">
              {[
                { label: 'Assigned', value: fmtDate(inspection.assignedAt) },
                { label: 'Last updated', value: fmtDate(inspection.updatedAt) },
                ...(inspection.completedAt
                  ? [{ label: 'Completed', value: fmtDate(inspection.completedAt) }]
                  : []),
                {
                  label: 'Attachments',
                  value: `${inspection.attachments.length} file${inspection.attachments.length !== 1 ? 's' : ''}`,
                },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">{label}</span>
                  <span className="text-xs font-semibold text-gray-800">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
